import { useMemo, useState } from 'react';
import { ProjectFormModal } from '../components/projects/ProjectFormModal';
import { ProjectListRow } from '../components/projects/ProjectListRow';
import { PageHeader } from '../components/ui/PageHeader';
import { PageToolbar } from '../components/ui/PageToolbar';
import { useCrmData } from '../contexts/CrmDataContext';
import { canCreatePersonalCrmRecords, hasElevatedBrowseUi } from '../lib/roleAccess';
import type { Project } from '../types/crm';
import { CustomersEnquiryExportButton } from '../components/projects/CustomersEnquiryExportButton';
import { clientById } from '../utils/crmHelpers';
import { latestUpdateForProject } from '../utils/projectHelpers';

type ProjectSortKey = 'status' | 'project' | 'account' | 'latest' | 'owner';
type ProjectSortDirection = 'asc' | 'desc';

const SORT_COLUMNS: { key: ProjectSortKey; label: string; managementLabel?: string }[] = [
  { key: 'status', label: 'Status' },
  { key: 'project', label: 'Project' },
  { key: 'account', label: 'Account' },
  { key: 'latest', label: 'Latest update', managementLabel: 'Recent updates (10)' },
  { key: 'owner', label: 'Owner' },
];

export function ProjectsPage() {
  const { data, getMyData, loading, refresh, ownerLabel, effectiveRole } = useCrmData();
  const exportCtx = useMemo(
    () => ({ data, ownerLabel }),
    [data, ownerLabel],
  );
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<ProjectSortKey>('latest');
  const [sortDir, setSortDir] = useState<ProjectSortDirection>('desc');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Project | null>(null);

  const canCreateProject = canCreatePersonalCrmRecords(effectiveRole);
  const canExportEnquiry = hasElevatedBrowseUi(effectiveRole);
  const isManagementList = effectiveRole === 'management';

  /** All projects for enquiry export (ignores search filter). */
  const allProjectsForExport = useMemo(() => {
    return [...getMyData('projects')].sort((a, b) =>
      (b.created_at ?? '').localeCompare(a.created_at ?? ''),
    );
  }, [getMyData]);

  const projects = useMemo(() => {
    let list = [...getMyData('projects')];

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((p) => {
        const client = clientById(data.clients, p.client_id);
        const pool =
          `${p.title} ${p.reference_no ?? ''} ${p.status ?? ''} ${client?.company_name ?? ''} ${ownerLabel(p.owner_id)}`.toLowerCase();
        return pool.includes(q);
      });
    }

    const dir = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'status') {
        cmp = (a.status ?? '').localeCompare(b.status ?? '', undefined, { sensitivity: 'base' });
      } else if (sortKey === 'project') {
        cmp = (a.title ?? '').localeCompare(b.title ?? '', undefined, { sensitivity: 'base' });
        if (cmp === 0) {
          cmp = (a.reference_no ?? '').localeCompare(b.reference_no ?? '', undefined, {
            sensitivity: 'base',
          });
        }
      } else if (sortKey === 'account') {
        const aName = clientById(data.clients, a.client_id)?.company_name ?? '';
        const bName = clientById(data.clients, b.client_id)?.company_name ?? '';
        cmp = aName.localeCompare(bName, undefined, { sensitivity: 'base' });
      } else if (sortKey === 'latest') {
        const aTime =
          latestUpdateForProject(data.updates, a.id)?.created_at ?? a.created_at ?? '';
        const bTime =
          latestUpdateForProject(data.updates, b.id)?.created_at ?? b.created_at ?? '';
        cmp = aTime.localeCompare(bTime);
      } else {
        cmp = ownerLabel(a.owner_id).localeCompare(ownerLabel(b.owner_id), undefined, {
          sensitivity: 'base',
        });
      }
      if (cmp !== 0) return cmp * dir;
      return (b.created_at ?? '').localeCompare(a.created_at ?? '');
    });
  }, [getMyData, sortKey, sortDir, search, data.clients, data.updates, ownerLabel]);

  function toggleSort(key: ProjectSortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDir(key === 'latest' ? 'desc' : 'asc');
  }

  return (
    <div className="projects-page">
      <PageHeader
        title="Projects"
        description="Track project progress, updates, and account ownership."
        actions={
          canCreateProject ? (
            <button
              type="button"
              className="btn-primary btn-sm"
              onClick={() => {
                setEditing(null);
                setModalOpen(true);
              }}
            >
              + New
            </button>
          ) : undefined
        }
      />

      <PageToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by title, reference, account or status"
        action={
          <div className="projects-page__toolbar-aside">
            <span className="page-count">
              {loading ? 'Loading…' : `${projects.length} project${projects.length === 1 ? '' : 's'}`}
            </span>
            <div className="page-actions-row__btns">
              {canExportEnquiry ? (
                <CustomersEnquiryExportButton
                  projects={allProjectsForExport}
                  ctx={exportCtx}
                  disabled={loading}
                />
              ) : null}
              <button
                type="button"
                className="btn-secondary btn-sm"
                onClick={() => void refresh()}
                disabled={loading}
              >
                Refresh
              </button>
            </div>
          </div>
        }
      />

      {loading && projects.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__spinner" />
          <p>Loading projects…</p>
        </div>
      ) : projects.length === 0 ? (
        <div className="empty-state">
          <p>No projects match your filters.</p>
        </div>
      ) : (
        <div className="project-list card">
          <div className="project-list__head" role="row">
            {SORT_COLUMNS.map(({ key, label, managementLabel }) => {
              const active = sortKey === key;
              const ariaSort = active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none';
              return (
                <button
                  key={key}
                  type="button"
                  className={`project-list__sort-btn${active ? ' is-active' : ''}`}
                  onClick={() => toggleSort(key)}
                  aria-sort={ariaSort}
                >
                  <span>{isManagementList && managementLabel ? managementLabel : label}</span>
                  {active ? (
                    <span className="project-list__sort-ind" aria-hidden>
                      {sortDir === 'asc' ? '▲' : '▼'}
                    </span>
                  ) : null}
                </button>
              );
            })}
            <span />
          </div>
          {projects.map((p) => (
            <ProjectListRow
              key={p.id}
              project={p}
              client={clientById(data.clients, p.client_id)}
              ownerName={ownerLabel(p.owner_id)}
              updates={data.updates}
              viewRole={effectiveRole}
              onReplyPosted={() => void refresh()}
            />
          ))}
        </div>
      )}

      <ProjectFormModal
        open={modalOpen}
        project={editing}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
      />
    </div>
  );
}
